import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './EventRegistration.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const EventRegistration = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/public/events/${eventNameId}`);
                setEvent(response.data);
            } catch (err) {
                setError('Event not found');
            } finally {
                setLoading(false);
            }
        };

        if (eventNameId) {
            fetchEvent();
        }
    }, [eventNameId]);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        if (value && !validateEmail(value)) {
            setEmailError('Please enter a valid email address');
        } else {
            setEmailError('');
        }
    };

    const handleNext = async () => {
        if (!email) {
            setEmailError('Required');
            return;
        }

        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/public/event-registrations/`, {
                email: email,
                event_name_id: eventNameId
            });

            // Store the EventRegistrationGuid and email in localStorage
            localStorage.setItem('eventRegistrationGuid', response.data.EventRegistrationGuid);
            localStorage.setItem('userEmail', email);

            // Navigate to the registration type selection page
            navigate(`/event/${eventNameId}/registration/type`);
        } catch (err) {
            if (err.response?.data?.detail === 'Email already registered for this event') {
                setEmailError('This email is already registered for this event');
            } else {
                setError('Registration failed. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };


    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    if (error || !event) {
        return <div className="page-error">Event not found</div>;
    }


    return (
        <div className="event-registration">
            {/* Event Banner with Logo */}
            <div className="event-banner">
                <img
                    src={event.EventImage || "/Funds4Charity.png"}
                    alt="Event Logo"
                    className="event-logo"
                />
            </div>

            {/* Event Title */}
            <div className="event-header">
                <h1 className="event-title">{event.Name}</h1>
            </div>

            {/* Event Details */}
            <div className="event-details">
                <div className="detail-item location-item">
                    <span className="detail-text">
                        <i className="fas fa-map-marker-alt detail-icon"></i>
                        {event.Location}
                        {event.Address1 && ` - ${event.Address1}`}
                        {event.City && `, ${event.City}`}
                        {event.StateProvince && `, ${event.StateProvince}`}
                        {event.Country && `, ${event.Country}`}
                    </span>
                </div>

                <div className="detail-item date-time-row">
                    <div className="date-section">
                        <i className="fas fa-calendar-alt detail-icon"></i>
                        <span className="detail-text">
                            {formatDate(event.StartDate)} - {formatDate(event.EndDate)}
                        </span>

                        <i className="fas fa-clock detail-icon"></i>
                        <span className="detail-text">
                            {formatTime(event.StartDate)} - {formatTime(event.EndDate)} {event.TimeZone || 'EDT'}
                        </span>
                    </div>

                </div>
            </div>

            {/* Separator */}
            <div className="separator"></div>

            {/* Registration Section */}
            <div className="registration-section">
                <h2 className="registration-prompt">Please register using your email address*</h2>

                <div className="form-group">
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={handleEmailChange}
                        className={`form-input ${emailError ? 'error' : ''}`}
                        placeholder="Enter your email address"
                        disabled={isSubmitting}
                    />
                    {emailError && <span className="error-message">{emailError}</span>}
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="navigation-buttons">
                <button
                    type="button"
                    className={`next-button ${email ? 'active' : ''}`}
                    onClick={handleNext}
                    disabled={!email || emailError}
                >
                    {isSubmitting ? 'Submitting...' : 'Next'}
                </button>
            </div>
        </div >
    );
};

export default EventRegistration;